import {
  ACTIVITY_CATEGORIES,
  ACTIVITY_CATEGORY_NAMES,
  categoryOf,
  type ActivityCategory,
  type ActivityType,
  type RecordEntity,
} from '@crm/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CheckSquare, FileText, Loader2, Mail, MessageSquare, PencilLine } from 'lucide-react';
import { useMemo, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type ActivityView } from '../../lib/client.ts';
import { Button } from '../ui/button.tsx';

const CHIP_LABEL: Record<ActivityCategory, string> = {
  notes: 'Notes',
  tasks: 'Tasks',
  changes: 'Changes',
  documents: 'Documents',
  messages: 'Messages',
};
const ICON = {
  notes: MessageSquare,
  tasks: CheckSquare,
  changes: PencilLine,
  documents: FileText,
  messages: Mail,
};

const show = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : typeof v === 'string' ? v : JSON.stringify(v);

/**
 * The Activity tab (RELAY-946): the record's timeline, newest first, one API read per page.
 * Chips filter by category (several at once; none = everything); each category is a set of
 * types, sent to the API as one `types` filter, so filtering never merges anything here.
 * It refreshes after every write through the client's write event, so a change made anywhere
 * shows up at once.
 */
export function ActivityTimeline({
  entityId,
  entityType = 'candidates',
}: {
  entityId: string;
  entityType?: RecordEntity;
}) {
  const [chips, setChips] = useState<ActivityCategory[]>([]);
  const types = useMemo<ActivityType[]>(
    () => chips.flatMap((c) => [...ACTIVITY_CATEGORIES[c]]),
    [chips],
  );
  const q = useInfiniteQuery({
    queryKey: ['activities', entityType, entityId, [...chips].sort().join(',')],
    queryFn: ({ pageParam }) =>
      api.activities(entityId, { types, ...(pageParam ? { cursor: pageParam } : {}) }, entityType),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const toggle = (c: ActivityCategory) =>
    setChips((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

  return (
    <div className="flex flex-col gap-3" data-testid="activity-timeline">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter activity">
        <Chip active={chips.length === 0} onClick={() => setChips([])}>
          All
        </Chip>
        {ACTIVITY_CATEGORY_NAMES.map((c) => (
          <Chip key={c} active={chips.includes(c)} onClick={() => toggle(c)}>
            {CHIP_LABEL[c]}
          </Chip>
        ))}
      </div>
      {q.isPending && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}
      {q.error != null && (
        <p className="text-danger text-sm" role="alert">
          {describeError(q.error)}
        </p>
      )}
      {!q.isPending && q.error == null && items.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {chips.length ? 'Nothing of that kind yet.' : 'No activity yet.'}
        </p>
      )}
      <ol className="flex flex-col gap-2" data-testid="activity-list">
        {items.map((a) => (
          <ActivityItem key={a.id} activity={a} />
        ))}
      </ol>
      {q.hasNextPage && (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => void q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-brand border-brand text-white'
          : 'border-border text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function ActivityItem({ activity: a }: { activity: ActivityView }) {
  const category = categoryOf(a.type);
  const Icon = ICON[category];
  const changes =
    a.type === 'field_changed'
      ? Object.entries(
          (a.payload?.changes ?? {}) as Record<string, { old?: unknown; new?: unknown }>,
        )
      : [];
  // An ingested email or message (TS-955): who it went to or came from, and a snippet.
  const message =
    category === 'messages'
      ? {
          via: typeof a.payload?.source === 'string' ? a.payload.source : null,
          detail: (a.payload?.payload ?? {}) as Record<string, unknown>,
        }
      : null;
  const who =
    a.source === 'user'
      ? (a.actor?.name ?? 'Former member')
      : a.source === 'system'
        ? 'System'
        : (a.actor?.name ?? 'Integration');
  return (
    <li className="flex gap-3 text-sm" data-testid="activity" data-type={a.type}>
      <Icon className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p>{a.summary}</p>
        {a.type === 'stage_changed' && (
          <p className="text-muted-foreground text-xs">
            {/* A pipeline move carries the stage ids in from/to and their names beside them. */}
            {show(a.payload?.fromName ?? a.payload?.from)} →{' '}
            <span className="text-foreground">{show(a.payload?.toName ?? a.payload?.to)}</span>
          </p>
        )}
        {message && (
          <p className="text-muted-foreground text-xs" data-testid="message-detail">
            {[
              typeof message.detail.to === 'string' ? `To ${message.detail.to}` : null,
              typeof message.detail.from === 'string' ? `From ${message.detail.from}` : null,
              typeof message.detail.snippet === 'string' ? `“${message.detail.snippet}”` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
        {changes.length > 0 && (
          <ul className="text-muted-foreground mt-0.5 text-xs">
            {changes.map(([field, c]) => (
              <li key={field}>
                {field.replace(/^custom\./, '')}: {show(c.old)} →{' '}
                <span className="text-foreground">{show(c.new)}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-muted-foreground text-xs">
          {who}
          {message?.via ? ` via ${message.via}` : ''} ·{' '}
          <time dateTime={a.occurredAt}>{new Date(a.occurredAt).toLocaleString()}</time>
        </p>
      </div>
    </li>
  );
}
